import type { ErrorCode } from './errors.js';
import { isErrorCode } from './errors.js';

/**
 * The universal request/response envelope from `106` §1.
 *
 * Architectural rule 3 (CLAUDE.md): every state-changing request carries a
 * client-generated `actionId`. A repeated `actionId` returns the original
 * result verbatim without re-executing.
 */
export interface ActionRequest<TPayload = unknown> {
  actionId: string;
  sessionId: string;
  payload: TPayload;
}

export interface ActionSuccess<TResult = unknown> {
  ok: true;
  result: TResult;
  stateVersion: number;
}

export interface ActionFailure {
  ok: false;
  error: ErrorCode;
  message: string;
  retryable: boolean;
}

export type ActionResponse<TResult = unknown> = ActionSuccess<TResult> | ActionFailure;

/** Canonical RFC 4122 v4 UUID, lower-case. */
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export function isActionId(value: unknown): value is string {
  return typeof value === 'string' && UUID_V4.test(value);
}

export function newActionId(): string {
  return crypto.randomUUID();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Validates an inbound envelope at the trust boundary.
 *
 * The server runs this before touching state: a malformed or missing
 * `actionId` must be rejected, never defaulted, or the idempotency guarantee
 * silently disappears.
 */
export function isActionRequest(value: unknown): value is ActionRequest {
  if (!isRecord(value)) return false;
  return isActionId(value.actionId) && typeof value.sessionId === 'string' && 'payload' in value;
}

export function isActionResponse(value: unknown): value is ActionResponse {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    return 'result' in value && typeof value.stateVersion === 'number';
  }
  if (value.ok === false) {
    return (
      isErrorCode(value.error) &&
      typeof value.message === 'string' &&
      typeof value.retryable === 'boolean'
    );
  }
  return false;
}

export function fail(error: ErrorCode, message: string, retryable = false): ActionFailure {
  return { ok: false, error, message, retryable };
}

export function succeed<T>(result: T, stateVersion: number): ActionSuccess<T> {
  return { ok: true, result, stateVersion };
}
